import { fetchTrace } from '../../services/requestTracer';
import clone from 'clone-deep';
import {
  SLOT_QUALIFICATION_ENDPOINT,
  SLOT_QUALIFICATION_INVARIANT_QUALIFIED,
  SLOT_QUALIFICATION_SLOT_QUALIFIED,
  SLOT_QUALIFICATION_DQ_STATUS,
  SLOT_QUALIFICATION_SELECTION,
  SLOT_QUALIFICATION_POD_POSITION,
  SLOT_QUALIFICATION_SLOT_POSITION,
  SLOT_QUALIFICATION_QUALIFIED_ADS,
  SLOT_QUALIFICATION_FILLER_ONLY,
  SLOT_QUALIFICATION_IN_NEXT_SEQUENCE,
  SLOT_QUALIFICATION_APP,
  SLOT_QUALIFICATION_SESSION_TYPE,
  SLOT_QUALIFICATION_CONTENT_TYPE,
  SLOT_QUALIFICATION_CONTENT_RATING,
  SLOT_QUALIFICATION_REQUEST_BODY,
  SLOT_QUALIFICATION_RESPONSE_BODY,
  INVARIANT_QUALIFICATION_REQUEST_BODY,
  INVARIANT_QUALIFICATION_RESPONSE_BODY,
  INVARIANT_QUALIFICATION_ENDPOINT,
  INVARIANT_QUALIFICATION_AD_ID,
  INVARIANT_QUALIFICATION_PROXY_URL,
  INVARIANT_QUALIFICATION_QUALIFIED_ADS,
  INVARIANT_QUALIFICATION_DQ_STATUS,
  INVARIANT_QUALIFICATION_DQ_FILTER,
  INVARIANT_QUALIFICATION_DQ_DIMENSION,
  INVARIANT_QUALIFICATION_RESTRICTION_RULES,
  INVARIANT_QUALIFICATION_APP,
  INVARIANT_QUALIFICATION_SESSION_TYPE,
  INVARIANT_QUALIFICATION_CONTENT_TYPE,
  INVARIANT_QUALIFICATION_CONTENT_RATING,
  SLOT_QUALIFICATION_PROXY_URL,
  SLOT_QUALIFICATION_DQ_FILTER,
  SLOT_QUALIFICATION_DQ_DIMENSION,
  SLOT_QUALIFICATION_ADS_IN_SEQUENCE,
  SLOT_QUALIFICATION_DQ_REASON,
  INVARIANT_QUALIFICATION_DQ_REASON,
  SLOT_QUALIFICATION_AD_ID,
} from '../../pages/RequestTracer/constants';

const INITIAL_TRACE_STATE = {
  traceData: null,
  error: null,
};

export const requestTracer = {
  namespace: 'requestTracer',
  state: clone(INITIAL_TRACE_STATE),
  reducers: {
    updateTraceData(state, { payload: data }) {
      const newState = clone(state);
      newState.traceData = data;
      newState.error = null;
      return newState;
    },
    updateTraceError(state, { payload: error }) {
      const newState = clone(state);
      newState.error = error;
      return newState;
    },
  },
  effects: {
    *fetchTraces({ payload: { traceId, adId } }, { call, put }) {
      try {
        const response = yield call(fetchTrace, traceId, adId);
        if (response && response.data) {
          const normalized = normalizeResponse(response.data);
          const mappedData = {
            ...normalized,
            slotQualification: mapSlotQualification(normalized.slotQualification, normalized.invariantQualification),
            invariantQualification: mapInvariantQualification(normalized.invariantQualification),
            pauseQualification: mapPauseQualification(normalized.pauseQualification),
          };
          yield put({ type: 'updateTraceData', payload: mappedData });
        } else {
          yield put({
            type: 'updateTraceError',
            payload: { errorMessage: 'Fetching traces failed.', errorDetail: 'No data received from server.' },
          });
        }
      } catch (error) {
        const errorName = error.response?.data?.name || error.name || 'Error';
        // Only set errorName, do not set errorMessage or trigger any popup
        yield put({
          type: 'updateTraceError',
          payload: { errorName },
        });
      }
    },
  },
};

function getInvariantQualificationInResponse(invariantQualificationItem = {}) {
  const resp = invariantQualificationItem['qualification-response-details'] || {};
  return resp['in-response'] ?? '';
}

const mapSlotQualification = (slotQualification = [], invariantQualification = []) =>
  slotQualification.map((item = {}, idx) => {
    const resp = item['qualification-response-details'] || {};
    const dqReason =
      [resp[SLOT_QUALIFICATION_DQ_FILTER], resp[SLOT_QUALIFICATION_DQ_DIMENSION]]
        .filter((v) => v && v !== 'N/A')
        .join(', ') || 'N/A';
    const zipkinBlock = item['zipkin-block-details'] || {};
    const endpoint = zipkinBlock.endpoint ?? '';
    const reqDetails = item['qualification-request-details'] || {};
    const invariantInResponse = !!(
      Array.isArray(invariantQualification) &&
      invariantQualification[idx] &&
      getInvariantQualificationInResponse(invariantQualification[idx]) === true
    );
    return {
      [SLOT_QUALIFICATION_AD_ID]: item['ad-id'] ?? '',
      [SLOT_QUALIFICATION_ENDPOINT]: endpoint,
      [SLOT_QUALIFICATION_PROXY_URL]: reqDetails['proxy-url'] ?? '',
      [SLOT_QUALIFICATION_INVARIANT_QUALIFIED]: invariantInResponse,
      [SLOT_QUALIFICATION_SLOT_QUALIFIED]: resp['in-response'] ?? '',
      [SLOT_QUALIFICATION_DQ_STATUS]: resp['qualification-result'] ?? '',
      [SLOT_QUALIFICATION_SELECTION]: item['selection-round'] ?? '',
      [SLOT_QUALIFICATION_POD_POSITION]: item['pod-position'] ?? '',
      [SLOT_QUALIFICATION_SLOT_POSITION]: item['slot-number'] ?? '',
      [SLOT_QUALIFICATION_QUALIFIED_ADS]: resp['qualified-ad-count'] ?? '',
      [SLOT_QUALIFICATION_FILLER_ONLY]: item['filler-only'],
      [SLOT_QUALIFICATION_DQ_FILTER]: resp['disqualifying-filter'] ?? '',
      [SLOT_QUALIFICATION_DQ_DIMENSION]: resp['disqualifying-dimension'] ?? '',
      [SLOT_QUALIFICATION_IN_NEXT_SEQUENCE]: item['in-sequence'],
      [SLOT_QUALIFICATION_SESSION_TYPE]: reqDetails['session-type'] ?? '',
      [SLOT_QUALIFICATION_CONTENT_TYPE]: reqDetails['playback-type'] ?? '',
      [SLOT_QUALIFICATION_CONTENT_RATING]: reqDetails['content-rating'] ?? '',
      [SLOT_QUALIFICATION_REQUEST_BODY]: zipkinBlock['request-body'],
      [SLOT_QUALIFICATION_RESPONSE_BODY]: zipkinBlock['response-body'],
      [SLOT_QUALIFICATION_APP]: reqDetails['publisher'] ?? '',
      [SLOT_QUALIFICATION_ADS_IN_SEQUENCE]: resp['next-sequenced-ads'],
      [SLOT_QUALIFICATION_DQ_REASON]: dqReason,
    };
  });

const mapPauseQualification = (pauseQualification = []) =>
  pauseQualification.map((item = {}) => {
    const resp = item['qualification-response-details'] || {};
    const reqDetails = item['qualification-request-details'] || {};
    const zipkinBlock = item['zipkin-block-details'] || {};
    const dqReason =
      [resp[INVARIANT_QUALIFICATION_DQ_FILTER], resp[INVARIANT_QUALIFICATION_DQ_DIMENSION]]
        .filter((v) => v && v !== 'N/A')
        .join(', ') || 'N/A';
    return {
      [INVARIANT_QUALIFICATION_ENDPOINT]: zipkinBlock.endpoint ?? '',
      [INVARIANT_QUALIFICATION_PROXY_URL]: reqDetails['proxy-url'] ?? '',
      [INVARIANT_QUALIFICATION_QUALIFIED_ADS]: resp['qualified-ad-count'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_STATUS]: resp['qualification-result'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_FILTER]: resp['disqualifying-filter'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_DIMENSION]: resp['disqualifying-dimension'] ?? '',
      [INVARIANT_QUALIFICATION_REQUEST_BODY]: zipkinBlock['request-body'] ?? '',
      [INVARIANT_QUALIFICATION_RESPONSE_BODY]: zipkinBlock['response-body'] ?? '',
      [INVARIANT_QUALIFICATION_APP]: reqDetails['publisher'] ?? '',
      [INVARIANT_QUALIFICATION_SESSION_TYPE]: reqDetails['session-type'] ?? '',
      [INVARIANT_QUALIFICATION_CONTENT_TYPE]: reqDetails['playback-type'] ?? '',
      [INVARIANT_QUALIFICATION_CONTENT_RATING]: reqDetails['content-rating'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_REASON]: dqReason,
    };
  });

const mapInvariantQualification = (invariantQualification = []) =>
  invariantQualification.map((item = {}) => {
    const resp = item['qualification-response-details'] || {};
    const reqDetails = item['qualification-request-details'] || {};
    const zipkinBlock = item['zipkin-block-details'] || {};
    const restrictionRules = item['restriction-rules'] || [];
    const dqReason =
      [resp[INVARIANT_QUALIFICATION_DQ_FILTER], resp[INVARIANT_QUALIFICATION_DQ_DIMENSION]]
        .filter((v) => v && v !== 'N/A')
        .join(', ') || 'N/A';
    return {
      [INVARIANT_QUALIFICATION_ENDPOINT]: zipkinBlock.endpoint ?? '',
      [INVARIANT_QUALIFICATION_AD_ID]: item['ad-id'] ?? '',
      [INVARIANT_QUALIFICATION_PROXY_URL]: reqDetails['proxy-url'] ?? '',
      [INVARIANT_QUALIFICATION_QUALIFIED_ADS]: resp['qualified-ad-count'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_STATUS]: resp['qualification-result'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_FILTER]: resp['disqualifying-filter'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_DIMENSION]: resp['disqualifying-dimension'] ?? '',
      [INVARIANT_QUALIFICATION_RESTRICTION_RULES]: restrictionRules ?? '',
      [INVARIANT_QUALIFICATION_REQUEST_BODY]: zipkinBlock['request-body'] ?? '',
      [INVARIANT_QUALIFICATION_RESPONSE_BODY]: zipkinBlock['response-body'] ?? '',
      [INVARIANT_QUALIFICATION_APP]: reqDetails['publisher'] ?? '',
      [INVARIANT_QUALIFICATION_SESSION_TYPE]: reqDetails['session-type'] ?? '',
      [INVARIANT_QUALIFICATION_CONTENT_TYPE]: reqDetails['playback-type'] ?? '',
      [INVARIANT_QUALIFICATION_CONTENT_RATING]: reqDetails['content-rating'] ?? '',
      [INVARIANT_QUALIFICATION_DQ_REASON]: dqReason,
    };
  });

function normalizeResponse(data) {
  if (!data) return {};
  return {
    traceId: data['trace-id'] || data.traceId,
    adId: data['ad-id'] || data.adId,
    invariantQualification: Array.isArray(data['invariant-qualification'])
      ? data['invariant-qualification']
      : data.invariantQualification || [],
    slotQualification: Array.isArray(data['slot-qualification'])
      ? data['slot-qualification']
      : data.slotQualification || [],
    pauseQualification: Array.isArray(data['pause-qualification'])
      ? data['pause-qualification']
      : data.pauseQualification || [],
  };
}
